import { useWorkspace } from '@/features/workspaces/hooks';
import { ConnectorFlowDTO, ConnectorInstanceSafeDTO, CustomConnectorDto } from '@/models/connectors';
import { useCompanyValues } from '@/providers';
import { useGetConnectorFlowsQuery, useGetConnectorInstancesQuery, useGetWorkspaceConnectorsQuery } from '@/services/endpoints/workspace-connectors';
import { SMS_CONNECTOR_NAME, SMS_FLOW_NAMES, type SmsFlowRole } from './constants';

type ResolveSmsChannelArgs = {
  connectors?: CustomConnectorDto[];
  instances?: ConnectorInstanceSafeDTO[];
  /** When set (e.g. from SmsWorkspaceProvider), prefer this connector over discovery. */
  preferredConnectorId?: string;
};

export const resolveSmsFlow = (flows: ConnectorFlowDTO[] | undefined, connectorId: string | undefined, role: SmsFlowRole) => {
  if (!connectorId) {
    return undefined;
  }
  const names: readonly string[] = SMS_FLOW_NAMES[role];
  return flows?.find((flow) => flow.connectorId === connectorId && names.includes(flow.name));
};

export const resolveSmsChannel = ({ connectors, instances, preferredConnectorId }: ResolveSmsChannelArgs) => {
  const preferred = preferredConnectorId ? connectors?.find((connector) => connector.id === preferredConnectorId) : undefined;
  const discovered = connectors?.find((connector) => connector.name === SMS_CONNECTOR_NAME && connector.isAuthenticated);
  const connector = preferred?.isAuthenticated ? preferred : discovered;

  return {
    connector,
    isAuthenticated: Boolean(connector?.isAuthenticated),
    instanceId: instances?.find((instance) => instance.connectorId === connector?.id)?.id,
  };
};

/**
 * Resolves the authenticated Sms connector for this workspace (no SmsWorkspaceProvider required).
 */
export const useSmsChannelConnector = (options?: { connectorId?: string }) => {
  const workspace = useWorkspace();
  const { currentLanguage } = useCompanyValues();

  const { data: connectors, isLoading: isConnectorsLoading } = useGetWorkspaceConnectorsQuery(
    { workspaceId: workspace.id, lang: currentLanguage },
    { skip: !workspace.id }
  );

  const { data: instances, isLoading: isInstancesLoading } = useGetConnectorInstancesQuery({ workspaceId: workspace.id }, { skip: !workspace.id });

  return {
    ...resolveSmsChannel({ connectors, instances, preferredConnectorId: options?.connectorId }),
    isLoading: isConnectorsLoading || isInstancesLoading,
  };
};

/**
 * The flow serving one role on the Sms connector, plus the instance to run it against.
 */
export const useSmsChannelFlow = (role: SmsFlowRole, options?: { connectorId?: string }) => {
  const workspace = useWorkspace();
  const channel = useSmsChannelConnector(options);

  const { data: flows, isLoading: isFlowsLoading } = useGetConnectorFlowsQuery(
    { workspaceId: workspace.id, queryParams: { showUserJourney: true } },
    { skip: !workspace.id }
  );

  return {
    ...channel,
    flow: resolveSmsFlow(flows, channel.connector?.id, role),
    connectorInstanceId: channel.instanceId,
    isLoading: channel.isLoading || isFlowsLoading,
  };
};
